<template>
  <v-container fluid v-if="slideshow">
    <v-row>
      <v-col cols="12">
        <v-sheet elevation="1" class="pa-4 mb-6">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-btn text icon @click="$router.push({ name: 'MilestoneSlideshowsRoute' })">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h2 class="mb-0">Slideshow: {{ slideshow ? slideshow.title : '' }}</h2>
            </div>
            <div class="d-flex align-center gap-4">
              <v-checkbox
                v-model="slideshow.active"
                label="Active"
                dense
                hide-details
                @change="saveActive"
                class="mr-4"
              ></v-checkbox>
              <v-btn
                color="primary"
                depressed
                @click="openAddSlide"
              >
                Add Slide
              </v-btn>
              <v-btn
                color="error"
                depressed
                :loading="removePending"
                :disabled="!slideshow"
                @click="confirmDelete = true"
              >
                Delete
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Upload New Slide -->
    <v-dialog v-model="uploadDialog" max-width="400">
      <v-card>
        <v-card-title>Upload New Slide</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newSlideDisplayTime"
            label="Display Time (seconds)"
            type="number"
            outlined
            dense
            clearable
            min="1"
          ></v-text-field>
          <v-text-field
            v-model.number="newSlideOrder"
            label="Order"
            type="number"
            outlined
            dense
            min="0"
          ></v-text-field>
          <v-file-input
            ref="fileupload"
            label="Upload Image"
            outlined
            dense
            show-size
            accept="image/*"
            prepend-icon="mdi-camera"
            @change="onFileChange"
            :loading="uploadLoading"
          ></v-file-input>
          <v-col cols="12" v-if="uploadedFile">
              <v-responsive>
                <v-img :src="uploadedFile.fileUrl" />
              </v-responsive>
            </v-col>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="uploadDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed @click="addSlide">Add Slide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Existing Slides List -->
    <v-row v-if="slidesLoading">
      <v-col class="text-center">
        <heart-loader />
      </v-col>
    </v-row> 

    <v-row v-if="currentSlides && currentSlides.length">
      <v-col cols="12">
        <v-sheet elevation="1" class="pa-4">
          <h3 class="mb-4">Slides ({{ currentSlides.length }})</h3>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Order</th>
                <th>Thumbnail</th>
                <th>Display Time</th>
                <th style="width: 120px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(slide) in sortedSlides" :key="slide.id">
                <td class="text--secondary">{{ slide.order !== undefined ? slide.order : '-' }}</td>
                <td>
                  <v-img
                    v-if="slide.file_upload"
                    :src="slide.file_upload.fileUrl"
                    max-width="80"
                    max-height="60"
                    contain
                    class="rounded"
                  />
                  <span v-else class="text--secondary text-caption">No image</span>
                </td>
                <td>{{ slide.display_time / 1000 || 0 }} sec</td>
                <td>
                  <v-btn x-small color="error" depressed @click="openDeleteSlideDialog(slide)">
                    Delete
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row v-if="!currentSlides || !currentSlides.length">
      <v-col class="text-center text--secondary">
        No slides yet. Upload an image above to add your first slide.
      </v-col>
    </v-row>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDelete" max-width="400">
      <v-card>
        <v-card-title>Delete Slideshow?</v-card-title>
        <v-card-text>
          This will permanently delete this slideshow and all of its slides. This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDelete = false">Cancel</v-btn>
          <v-btn color="error" depressed :loading="removePending" @click="deleteSlideshow">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDeleteSlide" max-width="400">
      <v-card>
        <v-card-title>Delete Slide?</v-card-title>
        <v-card-text>
          This will permanently delete this slide. This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteSlide = false">Cancel</v-btn>
          <v-btn color="error" depressed :loading="removePending" @click="deleteSlide">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import heartLoader from '@/components/heartLoader.vue'

export default {
  name: 'MilestoneSlideshowDetailPage',

  components: {
    heartLoader,
  },

  data() {
    return {
      file: null,
      uploadedFile: null,
      uploadLoading: false,
      confirmDelete: false,
      confirmDeleteSlide: false,
      deleteSlideId: null,
      newSlideTitle: '',
      newSlideDisplayTime: 10,
      newSlideOrder: null,
      newSlideFile: null,
      uploadDialog: false,
      uploadSuccess: false,
    }
  },

  computed: {
    ...mapGetters({
      slides: 'milestone-slide/list'
    }),
    slideshow_id() {
      return this.$route.params.id
    },
    slideshow() {
      return this.$store.state['milestone-slideshow'].keyedById[this.slideshow_id]
    },
    slidesLoading() {
      return this.$store.state['milestone-slide'].isFindPending
    },
    createPending() {
      return this.$store.state['milestone-slide'].isCreatePending
    },
    removePending() {
      return this.$store.state['milestone-slideshow'].isRemovePending
    },
    patchPending() {
      return this.$store.state['milestone-slideshow'].isPatchPending
    },
    currentSlides() {
      return this.slides.filter(slide => slide.slideshow_id == this.slideshow_id)
    },
    sortedSlides() {
      const slides = this.currentSlides.slice()
      slides.sort((a, b) => {
        const aOrder = a.order !== undefined ? a.order : 999999
        const bOrder = b.order !== undefined ? b.order : 999999
        return aOrder - bOrder
      })
      return slides
    },
  },

  methods: {
    async onFileChange(file) {
      this.file = file
      await this.uploadImage()
    },
    async uploadImage() {
      if (this.file) {
        this.formData = new FormData()
        this.formData.append('file', this.file)
        this.formData.append('eccoType', 'milestone-slideshow')
        this.uploadLoading = true
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/file-upload`, this.formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${this.$store.state.auth.accessToken}` }
        })

        this.uploadedFile = response.data
        this.uploadLoading = false
      }
    },
    async saveTitle() {
      if (!this.slideshow) return
      await this.$store.dispatch('milestone-slideshow/patch', [this.slideshow.id, {
        title: this.slideshow.title,
      }])
    },
    async saveActive() {
      if (!this.slideshow) return
      await this.$store.dispatch('milestone-slideshow/patch', [this.slideshow.id, {
        active: this.slideshow.active,
      }])
    },
    openAddSlide() {
      this.uploadDialog = true
      this.newSlideOrder = this.currentSlides.length > 0 ? Math.max(...this.currentSlides.map(s => s.order)) + 1 : 1
    },
    async addSlide() {
      if (!this.slideshow_id || !this.uploadedFile) return

      try {
        await this.$store.dispatch('milestone-slide/create', {
          layout: 'image',
          slideshow_id: this.slideshow_id,
          file_id: this.uploadedFile.id,
          title: this.newSlideTitle || null,
          order: this.newSlideOrder !== null && this.newSlideOrder !== undefined ? this.newSlideOrder : null,
          display_time: this.newSlideDisplayTime * 1000,
        })
        // Clear form
        this.newSlideTitle = ''
        this.newSlideOrder = null
        this.uploadedFile = null
        this.uploadSuccess = true
        this.uploadDialog = false
        await this.getSlides()
      } catch (error) {
        console.error('Error creating slide:', error)
      }
    },
    openDeleteSlideDialog(slide) {
      this.confirmDeleteSlide = true
      this.deleteSlideId = slide.id
    },
    async deleteSlide() {
      await this.$store.dispatch('milestone-slide/remove', this.deleteSlideId)
      this.confirmDeleteSlide = false
      this.deleteSlideId = null
    },
    async deleteSlideshow() {
      await this.$store.dispatch('milestone-slideshow/remove', this.slideshow_id)
      this.confirmDelete = false
      this.$router.push({ name: 'MilestoneSlideshowsRoute' })
    },
    async getSlides() {
      await this.$store.dispatch('milestone-slide/find', {
        query: {
          slideshow_id: this.slideshow_id,
          $limit: 1000,
          $sort: { order: 1 },
        }
      })
    }
  },

  async beforeMount() {
    // Ensure slideshow data is loaded
    if (!this.slideshow && this.slideshow_id) {
      await this.$store.dispatch('milestone-slideshow/get', this.slideshow_id)
    }
    // Load slides filtered by this slideshow
    await this.getSlides()
  },
}
</script>

<style lang="scss">
.gap-4 {
  gap: 16px;
}
</style>
